import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { get, post } from 'apis/apiClient';
import { API_PATH } from 'apis/apiPath';
import { useTranslation } from 'react-i18next';
import { withRouteTransaction } from 'apm/withTransaction';

import { Bank, BankLoginCredentials, BanksResponse } from 'models/Bank';
import {
  CreateDepotTransfersPayload,
  CreateFullDepotTransfersPayload,
  CreateRegShieldDepotTransfersApiPayload,
  CreateRegShieldDepotTransfersPayload
} from 'components/HomePage/types';
import { Tenant } from 'models/Tenant';
import { RegShieldLocation, RegShieldToken } from 'models/RegShield';
import {
  CompleteRegShieldRegistration,
  CompleteRegShieldRegistrationPayload
} from 'components/RegShield/types';
import { PageLoader } from 'components/Loader/PageLoader';
import { getErrorMessage } from 'components/RegShield/errorCodes';
import { CreateDepotTransfersResponse } from 'models/CreateDepotTransfersResponse';

import { MUTATION_KEYS, QUERY_KEYS } from 'enums/queryKeys';
import { errorReasonKeys } from 'enums/errorReasonKeys';

import { routes } from 'routes/routes';

import { triggerToast } from 'utils/triggerToast';

import { HomePage } from './HomePage';

const HomePageContainer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenant = queryClient.getQueryData<Tenant>([QUERY_KEYS.TENANT]) as Tenant;
  const [selectedBank, setSelectedBank] = useState<Bank | undefined>();
  const [bankSearchQuery, setBankSearchQuery] = useState('');
  const [onRegShieldFinishCallbackData, setOnRegShieldFinishCallbackData] =
    useState<CompleteRegShieldRegistrationPayload>();

  /**
   * [GET] /banks?search_term=<query>
   */
  const { data: { banks = [] } = {} } = useQuery<BanksResponse, AxiosError<Bank>>({
    queryKey: [QUERY_KEYS.BANKS_SEARCH, { bankSearchQuery }],
    queryFn: async () => {
      return bankSearchQuery
        ? await get(`${API_PATH.BANKS_SEARCH}?search_term=${bankSearchQuery}`)
        : [];
    }
  });

  /**
   * [GET] /login_fields?sort_code=<sort_code>
   */
  const { data: bankLoginFields } = useQuery<
    BankLoginCredentials,
    AxiosError<BankLoginCredentials>
  >({
    queryKey: [QUERY_KEYS.BANK_LOGIN_FIELDS, { sort_code: selectedBank?.sort_code }],
    queryFn: async () => {
      if (!selectedBank?.demo_supported) return {};

      return await get(`${API_PATH.BANK_LOGIN_FIELDS}?sort_code=${selectedBank.sort_code}`);
    }
  });

  /**
   * [GET] /location?bank_code=<bank_code>
   */
  const { data: regShieldLocation, refetch: refetchRegShieldLocation } = useQuery<
    RegShieldLocation,
    AxiosError<RegShieldLocation>
  >({
    queryKey: [QUERY_KEYS.REG_SHIELD_LOCATION, { sort_code: selectedBank?.sort_code }],
    queryFn: async () => {
      if (!selectedBank?.regshield_supported) return {};

      return await get(`${API_PATH.REG_SHIELD_LOCATION}?bank_code=${selectedBank.sort_code}`);
    }
  });

  /**
   * [GET] /token?code=<regShield_code>
   */
  const { data: regShieldToken } = useQuery<RegShieldToken, AxiosError<RegShieldToken>>({
    queryKey: [QUERY_KEYS.REG_SHIELD_TOKEN, { onRegShieldFinishCallbackData }],
    queryFn: async () => {
      if (!onRegShieldFinishCallbackData?.code) return {};

      return await get(`${API_PATH.REG_SHIELD_TOKEN}?code=${onRegShieldFinishCallbackData.code}`);
    }
  });

  const onMutationSuccess = async (data: CreateDepotTransfersResponse) => {
    if (data.reason) {
      const errorKey = errorReasonKeys[data.reason];
      const errorMsg = errorKey ? t(errorKey) : t('common.error');
      triggerToast(errorMsg, 'ERROR');
    } else {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DEPOT_TRANSFER] });
      navigate(routes.personalData);
    }
  };

  /**
   * [POST] /create_depot_transfers
   */
  const { mutate: _createDepotTransfers, isPending: isCreateDepotTransfersLoading } = useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_DEPOT_TRANSFERS],
    mutationFn: (payload: CreateDepotTransfersPayload) =>
      post({ url: API_PATH.CREATE_DEPOT_TRANSFERS, payload }),
    onSuccess: onMutationSuccess
  });

  /**
   * [POST] /full_depot_transfers
   */
  const { mutate: _createFullDepotTransfers, isPending: isCreateFullDepotTransfersLoading } =
    useMutation({
      mutationKey: [MUTATION_KEYS.CREATE_FULL_DEPOT_TRANSFERS],
      mutationFn: (payload: CreateFullDepotTransfersPayload) =>
        post({ url: API_PATH.CREATE_FULL_DEPOT_TRANSFERS, payload }),
      onSuccess: onMutationSuccess
    });

  /**
   * [POST] /regshield_depot_transfers
   */
  const {
    mutate: _createRegShieldDepotTransfers,
    isPending: isCreateRegShieldDepotTransfersLoading
  } = useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_REG_SHIELD_DEPOT_TRANSFERS],
    mutationFn: (payload: CreateRegShieldDepotTransfersApiPayload) =>
      post({ url: API_PATH.CREATE_REG_SHIELD_DEPOT_TRANSFERS, payload }),
    onSuccess: onMutationSuccess
  });

  const searchBanks = (query: string) => {
    if (!query) {
      setSelectedBank(undefined);
    }
    setBankSearchQuery(query);
  };

  const selectBank = (sortCode: string) => {
    const foundBank = banks?.find(_bank => _bank.sort_code === sortCode);
    setSelectedBank(foundBank);
  };

  const createDepotTransfers = async (payload: CreateDepotTransfersPayload) => {
    await _createDepotTransfers(payload);
  };

  const createFullDepotTransfers = async (payload: CreateFullDepotTransfersPayload) => {
    await _createFullDepotTransfers(payload);
  };

  const createRegShieldDepotTransfers = async (data: CreateRegShieldDepotTransfersPayload) => {
    const { access_token: token } = regShieldToken ?? {};
    const regshieldUserId = onRegShieldFinishCallbackData?.userId;
    if (token && regshieldUserId) {
      await _createRegShieldDepotTransfers({ ...data, token, regshieldUserId });
    }
  };

  const onRegShieldFinish: CompleteRegShieldRegistration = data => {
    setOnRegShieldFinishCallbackData(data);
  };

  const onRegShieldRedirectChallenge = () => {};

  const onRegShieldError = async (errorCode: number, traceId: string) => {
    const errorMsg = getErrorMessage(errorCode, traceId);
    triggerToast(errorMsg, 'ERROR');
    await refetchRegShieldLocation();
  };

  const isLoading =
    isCreateDepotTransfersLoading ||
    isCreateFullDepotTransfersLoading ||
    isCreateRegShieldDepotTransfersLoading;

  return (
    <>
      {isLoading && <PageLoader />}
      <HomePage
        banks={banks}
        selectedBank={selectedBank}
        onRegShieldFinish={onRegShieldFinish}
        createDepotTransfers={createDepotTransfers}
        createFullDepotTransfers={createFullDepotTransfers}
        createRegShieldDepotTransfers={createRegShieldDepotTransfers}
        searchBanks={searchBanks}
        selectBank={selectBank}
        bankLoginCredentials={bankLoginFields}
        onRegShieldRedirectChallenge={onRegShieldRedirectChallenge}
        onRegShieldError={onRegShieldError}
        regShieldLocation={regShieldLocation?.location}
        tenant={tenant}
        isRegShieldRegistrationComplete={Boolean(regShieldToken?.access_token)}
        isLoading={isLoading}
      />
    </>
  );
};

export default withRouteTransaction('HomePage')(HomePageContainer);
